import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { userSchema } from './user.js';
import { lessonSchema } from './lesson.js';
import { learningPathSchema } from './learning-path.js';
import { studentSchema } from './student.js';
import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { paginatedResponse } from './util-schema-types.js';
import {
  StudentFeedbackIssue,
  StudentFeedbackIssueLabelMap,
  studentFeedbackIssueSchema,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
  studentFeedbackStatusSchema,
} from './student-feedback-shared.js';

// Re-export for backward compatibility
export {
  StudentFeedbackIssue,
  StudentFeedbackIssueLabelMap,
  studentFeedbackIssueSchema,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
  studentFeedbackStatusSchema,
};

export const plainStudentFeedbackSchema = z.object({
  id: z.string(),
  studentId: z.string(),
  studentFeedbackIssue: studentFeedbackIssueSchema,
  relevelNeeded: z.boolean(),
  learningPathId: z.string().nullable(),
  startingLessonId: z.string().nullable(),
  reviewerId: z.string(),
  note: z.string(),
  status: studentFeedbackStatusSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
});
export type PlainStudentFeedback = z.infer<typeof plainStudentFeedbackSchema>;

export const studentFeedbackSchema = plainStudentFeedbackSchema.extend({
  student: studentSchema,
  learningPath: learningPathSchema.nullable(),
  startingLesson: lessonSchema.nullable(),
  reviewer: userSchema,
  sessionFeedbacks: z
    .array(
      z.object({
        feedbackContent: z.string().nullable(),
        majorConcerns: z.string().nullable(),
        issues: z.array(z.string()),
      }),
    )
    .optional(),
});
export type StudentFeedback = z.infer<typeof studentFeedbackSchema>;

export const createStudentFeedbackOptionsSchema = z.object({
  studentId: z.string(),
  studentFeedbackIssue: studentFeedbackIssueSchema,
  relevelNeeded: z.boolean(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
  reviewerId: z.string(),
  note: z.string(),
  status: studentFeedbackStatusSchema,
});
export type CreateStudentFeedbackOptions = z.infer<typeof createStudentFeedbackOptionsSchema>;

export const updateStudentFeedbackOptionsSchema = createStudentFeedbackOptionsSchema.partial();
export type UpdateStudentFeedbackOptions = z.infer<typeof updateStudentFeedbackOptionsSchema>;

export const bulkUpdateStudentFeedbackOptionsSchema = z.object({
  studentFeedbackIds: z.array(z.string()),
  options: updateStudentFeedbackOptionsSchema,
});
export type BulkUpdateStudentFeedbackOptions = z.infer<typeof bulkUpdateStudentFeedbackOptionsSchema>;

export const getStudentFeedbacksSortableColumnSchema = studentFeedbackSchema.keyof().extract(['createdAtInstant']);
export type GetStudentFeedbacksSortableColumn = z.infer<typeof getStudentFeedbacksSortableColumnSchema>;

export const getStudentFeedbacksSortingSchema = sortingSchemaFrom(getStudentFeedbacksSortableColumnSchema);

export const getStudentFeedbacksSortOrderSchema = sortOrderSchemaFrom(getStudentFeedbacksSortableColumnSchema);
export type GetStudentFeedbacksSortOrder = z.infer<typeof getStudentFeedbacksSortOrderSchema>;

export const getStudentFeedbacksFilterSchema = z.object({
  query: z.string().optional(),
  studentId: z.string().optional(),
  studentFeedbackIssues: z.array(studentFeedbackIssueSchema).optional(),
  statuses: z.array(studentFeedbackStatusSchema).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export type GetStudentFeedbacksFilter = z.infer<typeof getStudentFeedbacksFilterSchema>;

export const getStudentFeedbacksModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentFeedbacksFilterSchema))
  .merge(getStudentFeedbacksSortingSchema);
export type GetStudentFeedbacksModifier = z.infer<typeof getStudentFeedbacksModifierSchema>;

export const getStudentFeedbacksResultsSchema = paginatedResponse.extend({
  studentFeedbacks: z.array(studentFeedbackSchema),
});
export type GetStudentFeedbacksResults = z.infer<typeof getStudentFeedbacksResultsSchema>;
